import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { withForm } from '../utils/wizardForm';
import {
  FilePlus2,
  FilePenLine,
  Scale,
  ScanLine,
  CirclePause,
  Gauge,
  Receipt,
  Clock,
  ArrowRight,
} from 'lucide-react';
import HeaderLogged from '../components/HeaderLogged';
import Footer from '../components/Footer';
import { PREFILE_PERIOD, isPrefileActive, isSeasonOpen } from '../utils/prefile';

function FutureTaxYearModal({ onConfirm, onCancel }) {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 px-4">
      <div className="bg-white shadow-lg max-w-lg w-full">
        <div className="bg-[#0F2C5E] text-white px-6 py-4">
          <h2 className="text-lg font-semibold">Future Tax Year</h2>
        </div>
        <div className="px-6 py-5 space-y-3 text-sm text-gray-800">
          <p>
            You have selected to Pre file your 2290 tax returns for the year{' '}
            <strong>2026 - 2027</strong>. Tax returns will be processed once IRS
            starts accepting 2290 returns for the year <strong>2026 - 2027</strong>.
            Click OK to continue.
          </p>
          <p className="text-red-600 font-semibold">
            Processing will begin July 1 - not before.
          </p>
        </div>
        <div className="px-6 py-4 flex justify-end gap-3">
          <button
            onClick={onCancel}
            className="bg-red-500 text-white px-5 py-2 hover:bg-red-600 transition"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            className="bg-[#0F2C5E] text-white px-5 py-2 hover:bg-blue-900 transition"
          >
            OK
          </button>
        </div>
      </div>
    </div>
  );
}

function FileForm() {
  const { id } = useParams(); // id компании
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);

  // Состояния для полей формы
  const [taxPeriod, setTaxPeriod] = useState('');
  const [firstUsedMonth, setFirstUsedMonth] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [isFinalReturn, setIsFinalReturn] = useState(false);
  const [showPrefileModal, setShowPrefileModal] = useState(false);
  
  // --- НАЧАЛО ИЗМЕНЕНИЙ ---
  // Определяем массив всех месяцев в году
  const allMonths = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
  ];

  // Получаем индекс текущего месяца (от 0 до 11)
  // const currentMonthIndex = new Date().getMonth();
  
  const currentDate = new Date();
  const currentMonthIndex = currentDate.getMonth(); // 0..11
  const currentYear = currentDate.getFullYear();
  const parseStartYear = (period) => {
    const m = period?.match(/^(\d{4})-\d{4}$/);
    return m ? Number(m[1]) : null;
  };
  const isMonthDisabled = (idx) => {
    if (!taxPeriod) return true;
    const julyIdx = allMonths.indexOf("July");
    // Pre-file (before July 1): only July is allowed (period starts July 1).
    if (isPrefileActive(taxPeriod)) return idx !== julyIdx;
    // 2026-2027 once the season is open: it runs Jul 2026 - Jun 2027.
    if (taxPeriod === PREFILE_PERIOD) {
      // Calendar 2026 (Jul-Dec): months July..current. Calendar 2027: all valid.
      if (currentYear === 2026) return idx < julyIdx || idx > currentMonthIndex;
      return false;
    }
    const startYear = parseStartYear(taxPeriod);
    if (!startYear) return true;
    if (startYear < currentYear) return false; // past period — all available
    // current year: available up to and including the current month
    return idx > currentMonthIndex;
  };

  // Создаем список доступных месяцев (от начала года до текущего месяца включительно)
  const availableMonths = allMonths.slice(0, currentMonthIndex + 1);
  // --- КОНЕЦ ИЗМЕНЕНИЙ ---

  // Типы поправок (amended return) — карточки под основными действиями.
  // value должен совпадать с amendType в BeginAmendedReturn.js
  const amendmentTypes = [
    {
      value: 'TGW',
      title: 'Weight Increase (TGW)',
      badge: 'Categories A–V',
      desc: 'Vehicle moved to a higher taxable gross weight category',
      Icon: Scale,
    },
    {
      value: 'VIN',
      title: 'VIN Correction',
      badge: 'Any category',
      desc: 'Fix a mistyped VIN on an accepted return',
      Icon: ScanLine,
    },
    {
      value: 'Suspension',
      title: 'Mark as Suspended',
      badge: 'To Category W',
      desc: 'Move vehicles to the tax-suspended category',
      Icon: CirclePause,
    },
    {
      value: 'Mileage',
      title: 'Mileage Limit Exceeded',
      badge: 'Category W',
      desc: 'Suspended vehicle passed the 5,000 / 7,500 mile limit',
      Icon: Gauge,
    },
  ];

  // Верхний бар этапов
  const steps = [
    { id: 1, label: 'Tax & Form' },
    { id: 2, label: 'Vehicles Tax' },
    { id: 3, label: 'IRS Payment Options' },
    { id: 4, label: 'Form Summary' },
    { id: 5, label: 'Payment and IRS Submission' },
    { id: 6, label: 'IRS Confirmation' },
  ];
  const currentStep = 1; // текущий этап

  const navigate = useNavigate();

  useEffect(() => {
    const fetchCompanyData = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(`/api/companies/${id}/`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) {
          throw new Error('Error loading company data');
        }
        const data = await res.json();
        setCompany(data);
      } catch (error) {
        console.error(error);
      } finally {
        setLoading(false);
      }
    };

    fetchCompanyData();
  }, [id]);
  useEffect(() => {
    if (!firstUsedMonth) return;
    const idx = allMonths.indexOf(firstUsedMonth);
    if (idx >= 0 && isMonthDisabled(idx)) {
      setFirstUsedMonth('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taxPeriod, firstUsedMonth]);

  const handleTaxPeriodChange = (value) => {
    setTaxPeriod(value);
    // Only show the "future tax year / pre-file" modal while still in pre-file
    // mode. After July 1 the period is current and submits immediately.
    if (isPrefileActive(value)) {
      setShowPrefileModal(true);
      setFirstUsedMonth('July');
    }
  };
  const handleNew2290Return = async (e) => {
    e.preventDefault();
    setErrorMessage('');

    if (!taxPeriod || !firstUsedMonth) {
      setErrorMessage('Please fill in all required fields.');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/companies/${id}/form2290/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          company: id,
          tax_period: taxPeriod,
          first_used_month: firstUsedMonth,
          status: 'incomplete',
          total_tax_due: 0,
          final_return: isFinalReturn 
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        console.error('Validation errors:', errorData);
        throw new Error('Error creating Form 2290');
      }

      const createdForm = await response.json();

      // Переходим на следующий этап — номер формы едет в адресе, не в localStorage
      navigate(withForm(`/company/${id}/file-form/vehicles-tax`, createdForm.id));
    } catch (error) {
      console.error(error);
      setErrorMessage('Failed to create Form 2290. Please try again.');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen bg-[#E4F2FF]">
        <p className="text-gray-700 text-lg">Loading...</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-screen bg-[#E4F2FF]">
      {showPrefileModal && (
        <FutureTaxYearModal
          onConfirm={() => setShowPrefileModal(false)}
          onCancel={() => {
            setShowPrefileModal(false);
            setTaxPeriod('');
            setFirstUsedMonth('');
          }}
        />
      )}
      {/* Header */}
      <HeaderLogged />
      {/* Progress bar */}
      <div className="bg-white shadow-md">
        <div className="max-w-6xl mx-auto px-4 py-3 flex flex-wrap justify-center gap-3">
          {steps.map(step => {
            const isCurrent = step.id === currentStep;
            const isNext    = step.id === currentStep + 1;
            const isCompleted = step.id < currentStep;

            const baseClasses = 'text-sm font-medium px-3 py-1 border';
            const visibleClasses = isCurrent || isNext || isCompleted
              ? 'block'
              : 'hidden sm:block';

            const colorClasses = isCurrent
              ? 'bg-[#0F2C5E] text-white border-[#0F2C5E]'
              : isCompleted
                ? 'bg-green-100 text-green-700 border-green-200'
                : isNext
                  ? 'bg-[#E4E8F0] text-[#0F2C5E] border-[#E4E8F0]'
                  : 'bg-gray-100 text-gray-600 border-gray-200';

            return (
              <div
                key={step.id}
                className={`${baseClasses} ${visibleClasses} ${colorClasses}`}
              >
                {step.label}
              </div>
            );
          })}
        </div>
      </div>
      {/* Основное содержимое */}
      <main className="flex-1 p-4 sm:p-8 flex justify-center items-start">
        <div className="w-full max-w-4xl bg-white shadow-md p-6 sm:p-8">
          {/* Заголовок и описание */}
          <h1 className="text-2xl font-bold text-[#0F2C5E] mb-2">Tax & Form</h1>
          <p className="text-gray-700 mb-4">
            Select your tax period from July 1 to June 30 for Form 2290.
            {company && (
              <span className="block mt-1 text-gray-600">
                Current Company: <strong>{company.name}</strong>
              </span>
            )}
          </p>

          <form onSubmit={handleNew2290Return} className="space-y-6">
            {/* Tax Period */}
            <div>
              <label htmlFor="taxPeriod" className="block font-semibold mb-1">
                Tax Period <span className="text-red-500">*</span>
              </label>
              <select
                id="taxPeriod"
                required
                value={taxPeriod}
                onChange={(e) => handleTaxPeriodChange(e.target.value)}
                className="border p-2 w-full focus:outline-none focus:ring-2 focus:ring-[#0F2C5E]"
              >
                <option value="">Select</option>
                <option value="2025-2026">2025-2026</option>
                <option value="2026-2027">
                  {isSeasonOpen() ? '2026-2027' : '2026-2027 (Pre-file)'}
                </option>
              </select>
            </div>

            {/* First Used Month */}
            <div>
              <label
                htmlFor="firstUsedMonth"
                className="block font-semibold mb-1"
              >
                First Used Month <span className="text-red-500">*</span>
              </label>
              <select
                id="firstUsedMonth"
                required
                value={firstUsedMonth}
                onChange={(e) => setFirstUsedMonth(e.target.value)}
                className="border p-2 w-full focus:outline-none focus:ring-2 focus:ring-[#0F2C5E]"
              >
                {/* --- НАЧАЛО ИЗМЕНЕНИЙ --- */}
              <option value=''>Select</option>
              {allMonths.map((month, idx) => {
                const disabled = isMonthDisabled(idx);
                return (
                  <option
                    key={month}
                    value={month}
                    disabled={disabled}                         // ↑ запрет выбора
                    className={disabled ? 'text-gray-400' : ''} // ↑ серый текст
                  >
                    {month}
                  </option>
                );
              })}
                {/* --- КОНЕЦ ИЗМЕНЕНИЙ --- */}
              </select>
            </div>

            {/* Final Return */}
            <div>
              <label htmlFor="finalReturn" className="block font-semibold mb-1">
                Final Return
              </label>
              <p className="text-sm text-gray-600 mb-2">
                Check this box if you no longer need to file Form 2290. Marking
                it as "Final Return" releases the tax obligations.
              </p>
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="finalReturn"
                  checked={isFinalReturn}
                  onChange={e => setIsFinalReturn(e.target.checked)}
                  className="h-5 w-5 text-[#0F2C5E] border-gray-300 focus:ring-[#0F2C5E]"
                />
                <label htmlFor="finalReturn" className="font-medium">
                  This is my final return
                </label>
              </div>
            </div>

            {/* Ошибки валидации */}
            {errorMessage && (
              <p className="text-red-500 text-sm">{errorMessage}</p>
            )}

            {/* Кнопки действий — карточки */}
            <div className="mt-8">
              {/* Основные действия */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <button
                  type="submit"
                  className="group relative text-left bg-white border-2 border-[#0F2C5E] rounded-xl p-5 pt-6 flex flex-col gap-2.5 transition hover:-translate-y-0.5 hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-[#0F2C5E] focus:ring-offset-2"
                >
                  <span className="absolute -top-3 left-4 bg-[#0F2C5E] text-white text-xs px-2.5 py-1 rounded-md">
                    Most common
                  </span>
                  <span className="w-11 h-11 rounded-lg bg-[#E4F2FF] flex items-center justify-center">
                    <FilePlus2 className="w-6 h-6 text-[#0F2C5E]" aria-hidden="true" />
                  </span>
                  <span className="font-semibold text-[#0F2C5E]">New 2290 Return</span>
                  <span className="text-sm text-gray-600">
                    File a new heavy vehicle use tax return for this period
                  </span>
                  <span className="mt-auto pt-1 text-sm font-semibold text-[#0F2C5E] inline-flex items-center gap-1">
                    Start
                    <ArrowRight
                      className="w-4 h-4 transition-transform group-hover:translate-x-1"
                      aria-hidden="true"
                    />
                  </span>
                </button>

                <div
                  aria-disabled="true"
                  className="relative bg-white border border-gray-300 rounded-xl p-5 pt-6 flex flex-col gap-2.5 opacity-60 cursor-not-allowed select-none"
                >
                  <span className="w-11 h-11 rounded-lg bg-gray-100 flex items-center justify-center">
                    <Receipt className="w-6 h-6 text-gray-500" aria-hidden="true" />
                  </span>
                  <span className="font-semibold text-[#0F2C5E]">New 8849 Return</span>
                  <span className="text-sm text-gray-600">
                    Claim a refund for sold, destroyed or stolen vehicles
                  </span>
                  <span className="mt-auto pt-1 text-xs text-gray-500 inline-flex items-center gap-1.5">
                    <Clock className="w-3.5 h-3.5" aria-hidden="true" />
                    Temporarily unavailable
                  </span>
                </div>
              </div>

              {/* Amended 2290 Return */}
              <div className="mt-8 mb-3 flex items-center gap-2.5">
                <FilePenLine className="w-5 h-5 text-[#0F2C5E]" aria-hidden="true" />
                <span className="font-semibold text-[#0F2C5E]">Amended 2290 Return</span>
                <span className="flex-1 h-px bg-[#B9D4EA]" aria-hidden="true" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                {amendmentTypes.map(({ value, title, badge, desc, Icon }) => (
                  <button
                    key={value}
                    type="button"
                    onClick={() => navigate(`/company/${id}/begin-amended?type=${value}`)}
                    className="group text-left bg-white border border-[#B9D4EA] rounded-xl p-4 flex flex-col gap-2 transition hover:border-[#0F2C5E] hover:-translate-y-0.5 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-[#0F2C5E] focus:ring-offset-2"
                  >
                    <span className="w-9 h-9 rounded-lg bg-[#E4F2FF] flex items-center justify-center">
                      <Icon className="w-5 h-5 text-[#0F2C5E]" aria-hidden="true" />
                    </span>
                    <span className="text-sm font-semibold text-[#0F2C5E] leading-snug">
                      {title}
                    </span>
                    <span className="self-start text-xs font-semibold text-[#0F2C5E] bg-[#E4F2FF] border border-[#B9D4EA] px-2 py-0.5 rounded">
                      {badge}
                    </span>
                    <span className="text-xs text-gray-600 leading-relaxed">{desc}</span>
                    <span className="mt-auto pt-1 text-xs font-semibold text-[#0F2C5E] inline-flex items-center gap-1 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 sm:group-focus-visible:opacity-100 transition-opacity">
                      Start
                      <ArrowRight className="w-3.5 h-3.5" aria-hidden="true" />
                    </span>
                  </button>
                ))}
              </div>
            </div>
          </form>
        </div>
      </main>

      {/* Footer */}
      <Footer />
    </div>
  );
}

export default FileForm;